import React, { useRef } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import HighchartsMore from "highcharts/highcharts-more";
import SolidGauge from "highcharts/modules/solid-gauge";
import type { ChartBaseProps } from "../types/chart-types";

// Initialize required modules
HighchartsMore(Highcharts);
SolidGauge(Highcharts);

/**
 * Props interface for LivisGaugeHighcharts component
 * Extends ChartBaseProps with gauge chart specific configuration
 */
interface LivisGaugeHighchartsProps extends ChartBaseProps {
  data: Array<{
    name: string;
    value: number;
    color?: string;
  }>;
  height?: number;
  minValue?: number;
  maxValue?: number;
  backgroundColor?: string;
  gaugeColor?: string;
  innerRadius?: string;
  outerRadius?: string;
  centerY?: string;
  startAngle?: number;
  endAngle?: number;
  showLabels?: boolean;
  showTooltip?: boolean;
  animationDuration?: number;
}

/**
 * LivisGaugeHighcharts - A customizable gauge chart component using Highcharts
 */
const LivisGaugeHighcharts: React.FC<LivisGaugeHighchartsProps> = ({
  data = [],
  height = 250,
  minValue = 0,
  maxValue = 100,
  backgroundColor = 'var(--livis-search-background-color)',
  gaugeColor = '#0857DE',
  innerRadius = '60%',
  outerRadius = '100%',
  centerY = '85%',
  startAngle = -90,
  endAngle = 90,
  showLabels = false,
  showTooltip = false,
  animationDuration = 1000,
  title,
  subtitle
}) => {
  const chartRef = useRef<HighchartsReact.RefObject>(null);

  // Get the first data point or default to 0
  const value = data[0]?.value ?? 0;
  const color = data[0]?.color ?? gaugeColor;

  const options: Highcharts.Options = {
    chart: {
      type: "solidgauge",
      height,
      backgroundColor: 'transparent',
    },

    title: {
      text: title || undefined
    },

    subtitle: {
      text: subtitle || undefined
    },

    pane: {
      center: ['50%', centerY],
      size: '160%',
      startAngle,
      endAngle,
      background: [{
        innerRadius,
        outerRadius,
        shape: 'arc',
        backgroundColor
      }]
    },

    tooltip: {
      enabled: showTooltip
    },

    yAxis: {
      min: minValue,
      max: maxValue,
      lineWidth: 0,
      tickWidth: 0,
      minorTickInterval: null,
      tickAmount: 0,
      labels: {
        enabled: showLabels
      },
      stops: [
        [0, color],
        [1, color]
      ]
    },

    plotOptions: {
      solidgauge: {
        dataLabels: {
          enabled: false
        },
        linecap: 'round',
        // rounded: true,
        innerRadius,
        radius: outerRadius,
        animation: {
          duration: animationDuration
        }
      }
    },

    series: [{
      type: 'solidgauge',
      name: data[0]?.name || 'Value',
      data: [value],
      animation: {
        duration: animationDuration
      }
    },],

    credits: {
      enabled: false
    }
  };

  console.log("data", data);

  return (
    <>
      <div className="livis-reports-analytics-gauge-container" style={{ height }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
        />
        <div className="livis-reports-analytics-gauge-center">
          <div className="livis-reports-analytics-gauge-value">
            {value}%
          </div>
          {/* <div className="livis-reports-analytics-gauge-label">
            This month
          </div> */}
        </div>
      </div >

    </>
  );
};

export default LivisGaugeHighcharts;